<template>
  <div class="mobile-tooltip-wrapper">
    <repayment-type-tooltip v-if="repayment.repaymentTypeTooltipText" :repayment="repayment" />
  </div>
  <div class="repayment-option-wrapper" :position="position">
    <div class="repayment-option-sections">
      <div class="repayment-option-section">
        <div class="repayment-option-header">
          {{
            repayment.repaymentTypeDetails?.name || repayment?.repaymentTypeName
          }}&nbsp;<repayment-type-tooltip
            class="desktop-tooltip-wrapper"
            v-if="repayment.repaymentTypeTooltipText"
            :repayment="repayment"
          />
        </div>
      </div>
      <div class="repayment-option-section repayment-option-payment-amounts-wrapper">
        <div class="payment-wrapper">
          <div class="payment in-school">
            <p>{{ repayment.monthlyInSchoolPaymentDisplay }}</p>
            <p>{{ inSchoolPaymentLabel }}</p>
          </div>
        </div>
        <div class="divider"></div>
        <div class="payment-wrapper">
          <div class="payment post-school">
            <p>{{ repayment.monthlyPostSchoolPaymentDisplay }}</p>
            <p>{{ postGradPaymentLabel }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="repayment-option-radio" v-if="position === 'center'">
      <base-form-field-radio
        ref="repaymentOptionRef"
        v-model="form.repaymentOption"
        @update:modelValue="updateRepaymentOption"
        :schema="schema.paymentOption"
        :options="[{ label: '', value: repayment }]"
        :checked="isSelected"
        :disabled="savingChanges"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineProps, defineEmits, PropType } from "vue";
import { useStore } from "vuex";
import { FormSchema } from "@/models/form";
import { RatePlanVM, RatePlanRepaymentVM } from "@/models/loanConfiguration";
import BaseFormFieldRadio from "@/components/v2/shared/BaseFormFieldRefiRadio.vue";
import RepaymentTypeTooltip from "@/components/pel-loan-configuration/RepaymentTypeTooltip.vue";

const store = useStore();

const props = defineProps({
  ratePlan: {
    type: Object as PropType<RatePlanVM>,
    required: true,
  },
  repayment: {
    type: Object as PropType<RatePlanRepaymentVM>,
    required: true,
  },
  isMobile: {
    type: Boolean,
    required: true,
  },
  active: {
    type: Boolean,
    required: true,
  },
  position: {
    type: String,
    default: "",
  },
});

const schema = ref<FormSchema>({
  paymentOption: {
    name: "paymentOption",
    type: "radio",
    required: true,
    hideLabel: true,
  },
});

const emit = defineEmits(["updateRepaymentOption"]);

interface Form {
  repaymentOption?: RatePlanRepaymentVM;
}

let form = ref<Form>({
  repaymentOption: undefined,
});

const repaymentOptionRef = ref<undefined | HTMLElement>();

const mobile = computed(() => props.isMobile);

const savingChanges = computed<boolean>(() => store.state.loanConfigurationModule.savingChanges);

const inSchoolPaymentLabel = computed(() =>
  mobile.value ? "Monthly" : `${props.repayment?.monthlyInSchoolPaymentLabel} Monthly Payments`,
);

const postGradPaymentLabel = computed(() =>
  mobile.value ? "Monthly" : `${props.repayment?.monthlyPostSchoolPaymentLabel} Monthly Payments`,
);

const selectedRatePlan = computed(() => store.state.loanConfigurationModule.selectedRatePlan);

const selectedRepaymentOption = computed(
  () => store.state.loanConfigurationModule.selectedRepaymentOption,
);

const isSelected = computed<boolean>(
  () =>
    selectedRatePlan.value &&
    selectedRepaymentOption.value &&
    selectedRatePlan.value.id === props.ratePlan?.id &&
    selectedRepaymentOption.value.repaymentTypeId === props.repayment.repaymentTypeId,
);

async function updateRepaymentOption(repayment: any) {
  if (repayment) {
    await store.dispatch("loanConfigurationModule/setSelectedRatePlan", props.ratePlan);
    await store.dispatch("loanConfigurationModule/setSelectedRepaymentOption", repayment);
  }
}

function setRepaymentOption() {
  if (isSelected.value) {
    form.value.repaymentOption = selectedRepaymentOption.value;
  }
}

onMounted(() => {
  setRepaymentOption();
});
</script>

<style scoped>
.mobile-tooltip-wrapper {
  display: none;
}

.repayment-option-wrapper {
  background-color: #ffffff;
  border-radius: 6px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px 15px;
}

.repayment-option-sections {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.repayment-option-header {
  color: #000000;
  font-family: var(--header-font);
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: 17.175px; /* 122.68% */
  width: 100%;
  margin-bottom: 12px;
  position: relative;
}

.repayment-option-section.repayment-option-payment-amounts-wrapper {
  display: flex;
  flex-direction: row;
  gap: 1rem;
}

.payment-wrapper .payment p {
  margin-bottom: 0;
}

.payment-wrapper .payment p:first-of-type {
  color: #48a842;
  font-family: var(--header-font);
  text-align: left;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 17.175px;
}

.payment-wrapper .payment p:last-of-type {
  color: #000;
  font-family: var(--header-font);
  font-size: 0.625rem;
  font-weight: var(--font-weight-normal);
  line-height: 21px;
  font-style: normal;
  text-align: left;
}

.mobile-tooltip-wrapper,
.divider {
  display: none;
}

.repayment-option-radio {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
}

@media (max-width: 576.98px) {
  .mobile-tooltip-wrapper {
    display: contents;
  }
  .repayment-option-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 11px 12px 12px 12px;
    cursor: pointer;
  }
  .desktop-tooltip-wrapper {
    display: none;
  }
  .repayment-option-sections {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
  .mobile-tooltip-wrapper {
    color: #000;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-right: 0rem;
    margin-bottom: 1rem;
    padding: 5px;
    position: relative;
  }

  .repayment-option-section {
    width: 100%;
    display: flex;
  }

  :nth-child(1 of .repayment-option-section) {
    max-width: 109px;
  }

  .repayment-option-header {
    font-size: 0.75rem;
    margin-bottom: 0;
    align-items: center;
  }

  .repayment-option-section.repayment-option-payment-amounts-wrapper .payment-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  .payment-wrapper .payment {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .payment-wrapper .payment p:first-child {
    font-size: 1.125rem;
    text-align: left;
  }

  .payment p:last-of-type {
    font-size: 0.75rem;
    line-height: 15px;
    text-align: center;
  }

  .divider {
    border-left: 1px solid #ced1d9;
    display: block;
    height: auto;
  }

  .repayment-option-radio {
    display: none;
  }
}

@media (max-width: 390px) {
  .repayment-option-wrapper {
    padding: 0px 12px 12px 12px;
  }

  .repayment-option-section.repayment-option-payment-amounts-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 0.5rem;
  }

  .payment-wrapper .payment,
  .payment-wrapper .payment p:last-of-type {
    text-align: center;
    line-height: 15px; /* 93.75% */
  }

  .repayment-option-sections {
    flex-direction: column;
  }

  .repayment-option-section {
    text-align: center;
    justify-content: space-evenly;
  }
  .repayment-option-header {
    padding-left: 0;
    margin-bottom: 6px;
  }

  :nth-child(1 of .repayment-option-section) {
    max-width: 100%;
  }
}
</style>
